import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X } from "lucide-react";
import { api, type UserAccount } from "../lib/api";
import { useMe } from "../App";
import { AppShell } from "../components/AppShell";
import { ExcludedIpsCard } from "../components/ExcludedIpsCard";
import { Card, ErrorBox, PageSpinner, Spinner } from "../components/ui";

export default function UsersPage() {
  const [showForm, setShowForm] = useState(false);
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<{ users: UserAccount[] }>("/api/users"),
  });
  const list = users.data?.users ?? [];

  return (
    <AppShell
      right={
        <button type="button" className="btn-primary" onClick={() => setShowForm((value) => !value)}>
          {showForm ? <X size={15} /> : <Plus size={15} />}
          {showForm ? "Batal" : "Pengguna Baru"}
        </button>
      }
    >
      <div className="mx-auto max-w-3xl space-y-4">
        {showForm && <CreateUserForm onDone={() => setShowForm(false)} />}

        {users.isLoading ? (
          <PageSpinner />
        ) : users.error ? (
          <ErrorBox message="Gagal memuat daftar pengguna." />
        ) : (
          list.map((user) => <UserCard key={user.id} user={user} canDelete={list.length > 1} />)
        )}

        <ExcludedIpsCard />
      </div>
    </AppShell>
  );
}

function CreateUserForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const create = useMutation({
    mutationFn: () => api.post("/api/users", { username, email, password }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onDone();
    },
  });

  return (
    <form
      className="card space-y-4 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <h2 className="text-sm font-semibold text-slate-800">Pengguna baru</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="label" htmlFor="new-username">
            Username
          </label>
          <input
            id="new-username"
            className="input"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="budi"
            minLength={3}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="new-email">
            Email
          </label>
          <input
            id="new-email"
            type="email"
            className="input"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="budi@contoh.com"
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="new-password">
            Password
          </label>
          <input
            id="new-password"
            type="password"
            className="input"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>
      </div>
      {create.error && <p className="text-sm text-red-600">{(create.error as Error).message}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn" onClick={onDone}>
          Batal
        </button>
        <button type="submit" className="btn-primary" disabled={create.isPending}>
          {create.isPending && <Spinner className="text-white" />}
          Simpan
        </button>
      </div>
    </form>
  );
}

function UserCard({ user, canDelete }: { user: UserAccount; canDelete: boolean }) {
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isSelf = me.data?.id === user.id;

  const [username, setUsername] = useState(user.username ?? "");
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/users/${user.id}`, {
        username,
        email,
        password: password || undefined,
        current_password: password ? currentPassword : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      if (isSelf) queryClient.invalidateQueries({ queryKey: ["me"] });
      setPassword("");
      setCurrentPassword("");
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/api/users/${user.id}`),
    onSuccess: () => {
      if (isSelf) {
        queryClient.clear();
        navigate("/login", { replace: true });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  return (
    <Card title={user.username ? `@${user.username}` : user.email}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor={`u-${user.id}`}>
              Username
            </label>
            <input
              id={`u-${user.id}`}
              className="input"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="opsional"
            />
          </div>
          <div>
            <label className="label" htmlFor={`e-${user.id}`}>
              Email
            </label>
            <input
              id={`e-${user.id}`}
              type="email"
              className="input"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor={`p-${user.id}`}>
              Password baru (opsional)
            </label>
            <input
              id={`p-${user.id}`}
              type="password"
              className="input"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              placeholder="Biarkan kosong jika tidak diganti"
            />
          </div>
          {isSelf && password && (
            <div>
              <label className="label" htmlFor={`c-${user.id}`}>
                Password saat ini
              </label>
              <input
                id={`c-${user.id}`}
                type="password"
                className="input"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
          )}
        </div>

        {save.error && <p className="text-sm text-red-600">{(save.error as Error).message}</p>}
        {remove.error && <p className="text-sm text-red-600">{(remove.error as Error).message}</p>}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {isSelf && <span>Akun Anda</span>}
            {saved && <span className="text-emerald-600">Tersimpan</span>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-danger"
              disabled={!canDelete || remove.isPending}
              title={canDelete ? "Hapus pengguna" : "Tidak bisa menghapus akun terakhir"}
              onClick={() => {
                if (window.confirm(`Hapus pengguna "${user.username ?? user.email}"?`)) remove.mutate();
              }}
            >
              <Trash2 size={15} />
              Hapus
            </button>
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending && <Spinner className="text-white" />}
              Simpan
            </button>
          </div>
        </div>
      </form>
    </Card>
  );
}
